import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import api from '../lib/api';
import ProductCard from '../components/ui/ProductCard';
import { THEMES } from '../lib/themes';

const SearchPage = () => {
  const [params, setParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(params.get('q') || '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/products');
        if (!cancelled && res.data?.success) {
          setAllProducts((res.data.data || []).filter((p) => p.isActive));
        }
      } catch {/* swallow */}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allProducts.filter((p) => {
      const haystack = [
        p.name,
        p.description,
        p.category,
        p.type,
        ...(p.tags || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [allProducts, query]);

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-12">
      <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-6">SEARCH</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setParams(query ? { q: query } : {});
        }}
        className="flex gap-2 mb-10 max-w-2xl"
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products, realms, vibes..."
          className="flex-grow p-4 border-4 border-black font-mono text-lg focus:outline-none focus:bg-yellow-50"
        />
        <button
          type="submit"
          className="bg-black text-white px-6 font-black uppercase border-4 border-black hover:bg-yellow-400 hover:text-black inline-flex items-center gap-2"
        >
          <Search size={20} /> GO
        </button>
      </form>

      {loading ? (
        <div className="text-xl font-bold animate-pulse">Loading…</div>
      ) : !query ? (
        <p className="text-sm font-mono opacity-60">Type a search query above.</p>
      ) : results.length === 0 ? (
        <div className="bg-white border-4 border-black p-12 text-center shadow-[8px_8px_0px_black]">
          <h2 className="text-3xl font-black mb-2">NO MATCHES</h2>
          <p className="text-gray-600">No products match "{query}".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {results.map((p) => (
            <ProductCard key={p._id} product={p} theme={THEMES[p.category]} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchPage;
